import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus,
  Users,
  Filter,
  MapPin,
  Copy,
  Check,
  CheckCircle2,
  ChevronRight,
  KeyRound,
  ScanLine,
  Search,
  Car,
} from "lucide-react";
import { MitarbeiterZugangPrint } from "@/components/MitarbeiterZugangPrint";
import { AccountPolicyBanner, usePolicyBanner } from "@/components/AccountPolicyBanner";
import { useOwnerSession } from "@/hooks/useOwnerSession";
import { getVehiclePictogramSrc } from "@/lib/vehicleCategoryIcons";

const FILTERS = [
  { key: "active", label: "Aktiv" },
  { key: "archived", label: "Archiviert" },
  { key: "all", label: "Alle" },
];

/**
 * Reveal-Dialog: zeigt nach „Neu & anzeigen" die vollständigen Zugangsdaten
 * (Login-URL, Firmen-Code, frisch erzeugter PIN) EINMALIG an. Der Unternehmer
 * kann nur den PIN oder die kompletten Zugangsdaten kopieren. Danach ist der
 * PIN nicht mehr abrufbar (nur neu erzeugbar).
 */
const MONTEUR_LOGIN_URL = `${process.env.REACT_APP_PUBLIC_APP_URL}/monteur/login`;

export function PinRevealDialog({ state, companyCode, onOpenChange }) {
  const [copied, setCopied] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const pin = state?.pin || "";
  const code = companyCode || "";

  const copyPin = async () => {
    try {
      await navigator.clipboard?.writeText(pin);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const copyAll = async () => {
    const text = `Login: ${MONTEUR_LOGIN_URL}\nFirmen-Code: ${code}\nPIN: ${pin}`;
    try {
      await navigator.clipboard?.writeText(text);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 1800);
      toast.success("Zugangsdaten kopiert.");
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  return (
    <Dialog open={!!state?.open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="pin-reveal-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <KeyRound className="w-5 h-5" /> Zugangsdaten für {state?.name}
          </DialogTitle>
          <DialogDescription>
            Bitte jetzt notieren oder kopieren — aus Sicherheitsgründen wird der
            PIN <strong>nur einmal</strong> angezeigt und ist danach nur neu
            erzeugbar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-1">
          <div
            className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
            data-testid="pin-reveal-login"
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">
              Login
            </div>
            <div className="font-heading text-sm font-semibold text-slate-700 break-all">
              {MONTEUR_LOGIN_URL}
            </div>
          </div>
          <div
            className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
            data-testid="pin-reveal-company-code"
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">
              Firmen-Code
            </div>
            <div className="font-heading text-sm font-semibold text-slate-700 tracking-wide">
              {code || "—"}
            </div>
          </div>
          <div className="rounded-2xl bg-slate-900 text-white px-6 py-5 text-center">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">
              PIN
            </div>
            <div
              className="font-heading text-4xl font-bold tabular-nums tracking-[0.3em]"
              data-testid="pin-reveal-value"
            >
              {pin}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={copyPin} data-testid="pin-reveal-copy">
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-1.5 text-emerald-600" />
                Kopiert
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1.5" />
                PIN kopieren
              </>
            )}
          </Button>
          <Button
            onClick={copyAll}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="pin-reveal-copy-all"
          >
            {copiedAll ? (
              <>
                <Check className="w-4 h-4 mr-1.5" />
                Kopiert
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1.5" />
                Zugangsdaten kopieren
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// CLARTEXT SPRACHE V1 – Phase 1: Mitarbeitersprache (Owner-Datenpflege).
// Reine Datenerfassung, Default Deutsch. Keine UI-Übersetzung in dieser Phase.
export const LANGUAGE_OPTIONS = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "Englisch" },
  { value: "pl", label: "Polnisch" },
  { value: "ro", label: "Rumänisch" },
  { value: "uk", label: "Ukrainisch" },
  { value: "bg", label: "Bulgarisch" },
  { value: "tr", label: "Türkisch" },
  { value: "hr", label: "Kroatisch" },
  { value: "ru", label: "Russisch" },
];

export function EmployeeForm({
  open,
  onOpenChange,
  initial,
  onSaved,
  einsatzbereiche,
  vehicleCategories,
}) {
  const navigate = useNavigate();
  const isEdit = !!initial;
  const isArchivedEmployee = !!initial?.archived;
  const [first, setFirst] = useState(initial?.first_name || "");
  const [last, setLast] = useState(initial?.last_name || "");
  const [selectedEbIds, setSelectedEbIds] = useState(
    () => new Set(initial?.einsatzbereich_ids || [])
  );
  const [photo, setPhoto] = useState(initial?.photo_url || "");
  const [language, setLanguage] = useState(initial?.preferred_language || "de");
  // CLARTEXT · Fuehrerscheinkontrolle · Block C (Feb 2026) —
  // Fahrer-Kennzeichnung + Fahrzeugkategorien (1b/2a/3c/4a/5a/7a).
  const [isDriver, setIsDriver] = useState(!!initial?.is_driver);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(
    () => new Set(initial?.driver_categories || [])
  );
  // Block G (Feb 2026 · §11.b): Wiedervorlage-Intervall pro Mitarbeiter,
  // Enum {3, 6, 9, 12}, Default 6. Wird nur gesendet, wenn Fahrer.
  const [reverificationInterval, setReverificationInterval] = useState(
    Number(initial?.driver_reverification_interval_months) || 6
  );
  const [saving, setSaving] = useState(false);
  const { policyError, handlePolicyError, clearPolicyError } = usePolicyBanner();

  const activeEinsatzbereiche = useMemo(
    () => einsatzbereiche.filter((e) => !e.archived),
    [einsatzbereiche]
  );

  // Falls der Mitarbeiter einen heute archivierten Einsatzbereich hat, trotzdem
  // anzeigen (sonst verliert man die Information beim ersten Edit). Read-only.
  const visibleEinsatzbereiche = useMemo(() => {
    const map = new Map(activeEinsatzbereiche.map((e) => [e.id, e]));
    for (const ebId of selectedEbIds) {
      if (!map.has(ebId)) {
        const fromAll = einsatzbereiche.find((e) => e.id === ebId);
        if (fromAll) map.set(ebId, fromAll);
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "de")
    );
  }, [activeEinsatzbereiche, einsatzbereiche, selectedEbIds]);

  const toggleEb = (id) => {
    setSelectedEbIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // CLARTEXT · Fuehrerscheinkontrolle · Block C (Feb 2026)
  // 3c: UI-Komfort. Bei "Kein Fahrer" -> Auswahl gesperrt UND intern geleert,
  // damit die abgesendete Payload konsistent ist. Backend erzwingt die Regel
  // zusaetzlich als Wahrheit (Defense-in-Depth).
  const toggleIsDriver = (nextChecked) => {
    const val = nextChecked === true || nextChecked === "indeterminate";
    setIsDriver(val);
    if (!val) {
      setSelectedCategoryIds(new Set());
    }
  };
  const toggleCategory = (identifier) => {
    if (!isDriver) return;
    setSelectedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(identifier)) next.delete(identifier);
      else next.add(identifier);
      return next;
    });
  };
  const sortedVehicleCategories = useMemo(() => {
    const list = Array.isArray(vehicleCategories) ? [...vehicleCategories] : [];
    list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    return list;
  }, [vehicleCategories]);

  const submit = async () => {
    if (!first.trim() || !last.trim()) {
      toast.error("Vor- und Nachname sind Pflicht.");
      return;
    }
    if (selectedEbIds.size === 0) {
      toast.error("Bitte mindestens einen Einsatzbereich wählen.");
      return;
    }
    setSaving(true);
    clearPolicyError();
    try {
      const payload = {
        first_name: first.trim(),
        last_name: last.trim(),
        einsatzbereich_ids: Array.from(selectedEbIds),
        photo_url: photo.trim() || null,
        preferred_language: language,
        // CLARTEXT · Fuehrerscheinkontrolle · Block C (1b/2a/3c/4a/7a)
        // 3c: Bei Kein-Fahrer werden die Kategorien clientseitig geleert.
        // Backend erzwingt dieselbe Regel zusaetzlich als Wahrheit.
        is_driver: isDriver,
        driver_categories: isDriver ? Array.from(selectedCategoryIds) : [],
        // Block G (Feb 2026): Wiedervorlage-Intervall wird nur mitgeschickt,
        // wenn Fahrer — bei !isDriver ist es fachlich irrelevant, das
        // Backend haelt den letzten Wert einfach fest.
        ...(isDriver
          ? { driver_reverification_interval_months: Number(reverificationInterval) }
          : {}),
      };
      if (isEdit) {
        const res = await api.updateEmployee(initial.id, payload);
        toast.success("Mitarbeiter aktualisiert.");
        if (res?.auto_assigned > 0) {
          toast.success(
            `${res.auto_assigned} Dokumentation${
              res.auto_assigned === 1 ? "" : "en"
            } automatisch zugewiesen.`
          );
        }
      } else {
        const res = await api.createEmployee(payload);
        toast.success("Mitarbeiter angelegt.");
        if (res?.auto_assigned > 0) {
          toast.success(
            `${res.auto_assigned} Dokumentation${
              res.auto_assigned === 1 ? "" : "en"
            } automatisch zugewiesen.`
          );
        }
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="employee-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Mitarbeiter bearbeiten" : "Mitarbeiter anlegen"}
          </DialogTitle>
          <DialogDescription>
            Vor- und Nachname sind Pflicht. Mindestens ein Einsatzbereich
            zuordnen – steuert, welche Welten automatisch vorgeschlagen werden.
          </DialogDescription>
        </DialogHeader>

        {policyError && (
          <AccountPolicyBanner
            error={policyError}
            onDismiss={clearPolicyError}
            className="mt-2"
          />
        )}

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="emp-first">Vorname *</Label>
              <Input
                id="emp-first"
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                data-testid="employee-first-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="emp-last">Nachname *</Label>
              <Input
                id="emp-last"
                value={last}
                onChange={(e) => setLast(e.target.value)}
                data-testid="employee-last-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Einsatzbereiche *</Label>
              <span
                className="text-xs text-slate-500"
                data-testid="employee-eb-count"
              >
                {selectedEbIds.size === 0
                  ? "Keiner gewählt"
                  : selectedEbIds.size === 1
                    ? "1 ausgewählt"
                    : `${selectedEbIds.size} ausgewählt`}
              </span>
            </div>
            {visibleEinsatzbereiche.length === 0 ? (
              <Card className="border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">Noch keine Einsatzbereiche angelegt.</p>
                <p className="mt-1">
                  Bitte zuerst in den Einstellungen mindestens einen Einsatzbereich
                  anlegen (Montage, Büro, Lager …).
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onOpenChange(false);
                    navigate("/unternehmer/einstellungen");
                  }}
                  className="mt-2 h-8"
                  data-testid="employee-form-goto-einstellungen"
                >
                  <MapPin className="w-3.5 h-3.5 mr-1.5" />
                  Zu den Einstellungen
                </Button>
              </Card>
            ) : (
              <div
                className="border border-slate-200 rounded-md divide-y divide-slate-100"
                data-testid="employee-eb-list"
              >
                {visibleEinsatzbereiche.map((eb) => {
                  const checked = selectedEbIds.has(eb.id);
                  return (
                    <label
                      key={eb.id}
                      htmlFor={`emp-eb-${eb.id}`}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${eb.archived ? "opacity-60" : "hover:bg-slate-50"}`}
                    >
                      <Checkbox
                        id={`emp-eb-${eb.id}`}
                        checked={checked}
                        onCheckedChange={() => toggleEb(eb.id)}
                        disabled={eb.archived && !checked}
                        data-testid={`employee-eb-checkbox-${eb.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {eb.name}
                          {eb.archived && (
                            <Badge
                              variant="outline"
                              className="ml-2 bg-slate-100 border-slate-200 text-slate-500 text-[10px] uppercase tracking-wider"
                            >
                              Archiviert
                            </Badge>
                          )}
                        </div>
                        {eb.description && (
                          <div className="text-xs text-slate-500 truncate">
                            {eb.description}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="emp-language">Sprache</Label>
            <select
              id="emp-language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              data-testid="employee-language-select"
              className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-slate-400"
            >
              {LANGUAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="emp-photo">Foto-URL (optional)</Label>
            <Input
              id="emp-photo"
              value={photo}
              onChange={(e) => setPhoto(e.target.value)}
              placeholder="https://…"
              data-testid="employee-photo-input"
            />
          </div>

          {/* CLARTEXT · Fuehrerscheinkontrolle · Block C (Feb 2026)
             1b: Payload-Erweiterung in Create + Update.
             3c: UI-Komfort disabled bei Kein-Fahrer, Backend erzwingt Wahrheit.
             5a: Fuer archivierte Mitarbeiter komplett read-only.
             BE-002: KEINE Fahrerlaubnisklassen-/FeV-Automatik. Der Mandant
             legt fest, welche Fahrzeugarten der Mitarbeiter betrieblich
             fahren soll. */}
          <div className="space-y-3" data-testid="employee-driver-section">
            <div className="flex items-start gap-3">
              <Car className="w-5 h-5 text-slate-500 mt-0.5" aria-hidden="true" />
              <div className="flex-1">
                <label
                  htmlFor="emp-is-driver"
                  className={`flex items-center gap-2 select-none ${
                    isArchivedEmployee ? "cursor-default" : "cursor-pointer"
                  }`}
                >
                  <Checkbox
                    id="emp-is-driver"
                    checked={isDriver}
                    onCheckedChange={toggleIsDriver}
                    disabled={isArchivedEmployee}
                    data-testid="employee-is-driver-checkbox"
                  />
                  <span className="text-sm font-medium text-slate-900">
                    Fahrer / Fahrzeugnutzung
                  </span>
                </label>
                <p className="text-xs text-slate-500 mt-1">
                  Der Mitarbeiter soll betrieblich Fahrzeuge nutzen. Welche
                  Fahrzeugarten – wird darunter ausgewählt.
                </p>
              </div>
            </div>

            {sortedVehicleCategories.length > 0 && (
              <div
                className={`grid grid-cols-2 gap-2 ${
                  isDriver ? "" : "opacity-40 pointer-events-none"
                }`}
                data-testid="employee-vehicle-categories-grid"
                aria-disabled={!isDriver}
              >
                {sortedVehicleCategories.map((cat) => {
                  const checked = selectedCategoryIds.has(cat.identifier);
                  const imgSrc = getVehiclePictogramSrc(cat.pictogram_key);
                  const disabled = !isDriver || isArchivedEmployee;
                  return (
                    <label
                      key={cat.identifier}
                      htmlFor={`emp-vehcat-${cat.identifier}`}
                      className={`border rounded-md p-2 flex flex-col items-center gap-2 text-center ${
                        disabled ? "cursor-default" : "cursor-pointer"
                      } ${
                        checked
                          ? "border-slate-900 bg-slate-50"
                          : "border-slate-200 hover:border-slate-400"
                      }`}
                      data-testid={`employee-vehicle-category-card-${cat.identifier}`}
                    >
                      <div className="w-full aspect-square bg-[#F0F0F0] rounded flex items-center justify-center overflow-hidden">
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={cat.pictogram_alt_text || cat.display_name}
                            className="w-full h-full object-contain"
                            loading="lazy"
                          />
                        ) : (
                          <Car
                            className="w-8 h-8 text-slate-400"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                      <div className="flex items-start gap-2 w-full">
                        <Checkbox
                          id={`emp-vehcat-${cat.identifier}`}
                          checked={checked}
                          onCheckedChange={() => toggleCategory(cat.identifier)}
                          disabled={disabled}
                          data-testid={`employee-vehicle-category-checkbox-${cat.identifier}`}
                        />
                        <span className="text-xs font-medium text-slate-800 text-left leading-tight">
                          {cat.display_name}
                        </span>
                      </div>
                      {cat.orientation_hint && (
                        <span className="text-[11px] text-slate-500 leading-snug">
                          {cat.orientation_hint}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}

            {/* Block G (Feb 2026 · §11.b): Wiedervorlage-Intervall */}
            {isDriver && (
              <div
                className="mt-3 rounded-md border border-slate-200 bg-white px-3 py-2"
                data-testid="employee-driver-reverification-section"
              >
                <Label
                  htmlFor="emp-fsk-interval"
                  className="text-xs font-semibold text-slate-700"
                >
                  Führerschein-Wiedervorlage
                </Label>
                <select
                  id="emp-fsk-interval"
                  value={reverificationInterval}
                  onChange={(e) =>
                    setReverificationInterval(Number(e.target.value))
                  }
                  disabled={isArchivedEmployee}
                  data-testid="employee-driver-reverification-interval-select"
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800 focus:border-slate-500 focus:outline-none disabled:opacity-60"
                >
                  <option value={3}>alle 3 Monate</option>
                  <option value={6}>alle 6 Monate (Standard)</option>
                  <option value={9}>alle 9 Monate</option>
                  <option value={12}>alle 12 Monate</option>
                </select>
                <p className="mt-1 text-[11px] text-slate-500">
                  Nach jeder Freigabe wird der Mitarbeiter in diesem Rhythmus
                  automatisch erneut zur Führerschein-Prüfung aufgefordert.
                </p>
              </div>
            )}

            {isArchivedEmployee && (
              <p
                className="text-xs text-slate-500 italic"
                data-testid="employee-driver-archived-hint"
              >
                Mitarbeiter ist archiviert – die Fahrer-Zuweisung ist nur
                lesbar. Zum Ändern bitte zuerst reaktivieren.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="employee-form-cancel"
          >
            Abbrechen
          </Button>
          <Button
            onClick={submit}
            disabled={saving || visibleEinsatzbereiche.length === 0}
            data-testid="employee-form-submit"
            className="bg-slate-900 hover:bg-slate-800"
          >
            {saving ? "Speichert…" : isEdit ? "Speichern" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// CLARTEXT · UI-REDESIGN Master-Detail (Aug 2026) — Ampel-Status.
//
// Reine LESENDE Ableitung aus den bestehenden Assignment-Daten (keine neuen
// Datenmodelle, kein Backend-Call). Aggregationsregeln identisch zur
// bisherigen 5-Zahlen-Anzeige; zusätzlich `ampel_amber` OHNE Doppelzählung:
// `action_category === "handlungsbedarf"` umfasst laut `shared.py`
// (derive_action_category) sowohl überfällige als auch bald fällige und
// unterminierte offene Aufgaben — für Amber zählen daher genau die
// Handlungsbedarf-Assignments, die NICHT bereits überfällig (= Rot) sind.
// ---------------------------------------------------------------------------
export function emptyAssignmentStats() {
  return {
    handlungsbedarf: 0,
    bald_faellig: 0,
    ueberfaellig: 0,
    geplant: 0,
    erledigt: 0,
    ampel_amber: 0,
  };
}

export function groupAssignmentStats(assignments) {
  const grouped = {};
  for (const a of assignments || []) {
    const g = grouped[a.employee_id] || emptyAssignmentStats();
    if (a.action_category === "handlungsbedarf") g.handlungsbedarf += 1;
    if (a.action_category === "geplant") g.geplant += 1;
    if (a.status === "erledigt") g.erledigt += 1;
    if (a.status === "ueberfaellig") g.ueberfaellig += 1;
    if (a.status === "offen" && a.due_date) {
      const d = Math.floor((new Date(a.due_date) - new Date()) / 86400000);
      if (d >= 0 && d <= 30) g.bald_faellig += 1;
    }
    if (a.action_category === "handlungsbedarf" && a.status !== "ueberfaellig") {
      g.ampel_amber += 1;
    }
    grouped[a.employee_id] = g;
  }
  return grouped;
}

// Betreiber-Regel (Freigabe Aug 2026):
//   Rot   + Zahl: überfällig > 0 (Zahl = überfällig)
//   Amber + Zahl: sonst bald_fällig ODER Handlungsbedarf > 0
//                 (Zahl = distinkte betroffene Assignments, keine Doppelzählung)
//   Grün  + Häkchen: sonst
export function deriveAmpel(stats = {}) {
  const ueberfaellig = stats.ueberfaellig || 0;
  const amber = stats.ampel_amber || 0;
  if (ueberfaellig > 0) return { color: "rot", count: ueberfaellig };
  if (amber > 0) return { color: "amber", count: amber };
  return { color: "gruen", count: 0 };
}

const AMPEL_STYLES = {
  rot: {
    src: "/ampel/ampel-rot.png",
    badge: "bg-rose-600",
    label: "Überfällige Aufgaben",
    alt: "Ampel Rot — überfällige Aufgaben",
  },
  amber: {
    src: "/ampel/ampel-amber.png",
    badge: "bg-amber-500",
    label: "Fällige oder anstehende Aufgaben",
    alt: "Ampel Amber — anstehende Aufgaben",
  },
  gruen: {
    src: "/ampel/ampel-gruen.png",
    badge: "",
    label: "Alles im Plan",
    alt: "Ampel Grün — alles im Plan",
  },
};

// UI-REDESIGN Ergänzung Ampel-Fix (Aug 2026, betreibergenehmigt):
// Der bisherige rot-getönte Kreis mit User-Icon wird durch echte Ampellicht-
// Fotos (freigestellte PNGs unter /ampel/) ersetzt. Die Zahl (Rot=überfällig,
// Amber=bald_fällig∪Handlungsbedarf) wird als überlagerndes Badge angezeigt;
// bei Grün ein Häkchen. Logik unverändert (deriveAmpel). Sidebar-Icon
// "Mitarbeiter" bleibt separat und wird nicht angetastet.
export function AmpelStatus({ stats, employeeId }) {
  const ampel = deriveAmpel(stats);
  const style = AMPEL_STYLES[ampel.color];
  return (
    <div
      className="relative inline-flex items-center justify-center"
      title={style.label}
      data-testid={`employee-ampel-${employeeId}`}
      data-ampel={ampel.color}
    >
      <img
        src={style.src}
        alt={style.alt}
        className="w-14 h-14 object-contain select-none"
        draggable="false"
      />
      {ampel.color === "gruen" ? (
        <CheckCircle2
          className="absolute -right-0.5 -bottom-0.5 w-5 h-5 text-emerald-600 bg-white rounded-full"
          data-testid={`employee-ampel-check-${employeeId}`}
        />
      ) : (
        <span
          className={`absolute -right-1 -top-1 min-w-[1.375rem] h-6 px-1.5 rounded-full text-white text-[12px] font-semibold inline-flex items-center justify-center shadow-sm ring-2 ring-white ${style.badge}`}
          data-testid={`employee-ampel-count-${employeeId}`}
        >
          {ampel.count}
        </span>
      )}
    </div>
  );
}

export default function Mitarbeiter() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [einsatzbereiche, setEinsatzbereiche] = useState([]);
  const [assignmentsByEmp, setAssignmentsByEmp] = useState({});
  // CLARTEXT · Fuehrerscheinkontrolle · Block C — Read-Only 6 Kategorien
  // vom Backend. Wird beim Seitenladen 1x gezogen; Vertrag ist eingefroren.
  const [vehicleCategories, setVehicleCategories] = useState([]);
  const [filter, setFilter] = useState("active");
  // UI-REDESIGN (Aug 2026): Suchfeld oberhalb der Liste (Muster
  // Unterweisungen.jsx / library-search-input). Rein clientseitig.
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const { session } = useOwnerSession();
  const [zugangPrintOpen, setZugangPrintOpen] = useState(false);

  const reload = () =>
    Promise.all([
      api.listEmployeesAll(),
      api.listAssignments(),
      api.listEinsatzbereiche(true),
    ]).then(([emps, asns, ebs]) => {
      setEmployees(emps);
      setEinsatzbereiche(ebs);
      setAssignmentsByEmp(groupAssignmentStats(asns));
    });

  useEffect(() => {
    reload();
    // CLARTEXT · Fuehrerscheinkontrolle · Block C — Kategorien einmalig
    // vom Backend laden; Vertrag ist eingefroren, daher kein Reload noetig.
    api
      .getVehicleCategories()
      .then((cats) => setVehicleCategories(Array.isArray(cats) ? cats : []))
      .catch(() => {
        // Fail-closed im UI: bei Fehler leer -> Fahrer-Panel bleibt bedienbar
        // (Toggle), aber die 6 Karten werden nicht gerendert. Backend bleibt
        // die Wahrheit; Payload ohne Kategorien ist erlaubt.
        setVehicleCategories([]);
      });
  }, []);

  const filtered = useMemo(() => {
    let list = employees;
    if (filter === "archived") list = list.filter((e) => e.archived);
    else if (filter === "active") list = list.filter((e) => !e.archived);
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      list = list.filter((e) =>
        `${e.first_name || ""} ${e.last_name || ""}`.toLowerCase().includes(term)
      );
    }
    return list;
  }, [employees, filter, searchTerm]);

  const counts = useMemo(() => {
    const active = employees.filter((e) => !e.archived).length;
    const archived = employees.filter((e) => e.archived).length;
    return { active, archived, all: employees.length };
  }, [employees]);

  const onCreate = () => {
    setDialogOpen(true);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="mitarbeiter-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
            Team
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
            Mitarbeiter
          </h1>
          <p className="text-slate-600 mt-2">
            Anlegen, bearbeiten, archivieren. Jeder Mitarbeiter bekommt einen
            persönlichen 4-stelligen PIN für die Anmeldung.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setZugangPrintOpen(true)}
            variant="outline"
            data-testid="employee-print-access-button"
            className="h-11 px-5"
          >
            <ScanLine className="w-4 h-4 mr-2" />
            Mitarbeiterzugang drucken
          </Button>
          <Button
            onClick={onCreate}
            data-testid="employee-add-button"
            className="bg-slate-900 hover:bg-slate-800 h-11 px-5"
          >
            <Plus className="w-4 h-4 mr-2" />
            Mitarbeiter hinzufügen
          </Button>
        </div>
      </div>

      {/* Suche + Filter */}
      <Card className="bg-white border border-slate-200 shadow-sm p-3 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Mitarbeiter suchen…"
            className="pl-9"
            data-testid="employee-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500 ml-2" />
          <div className="flex gap-1">
            {FILTERS.map((f) => {
              const c =
                f.key === "active"
                  ? counts.active
                  : f.key === "archived"
                    ? counts.archived
                    : counts.all;
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`employee-filter-${f.key}`}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-2 ${
                    active
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {f.label}
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded ${
                      active ? "bg-white/10" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {c}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Liste (schlank: Name + Ampel, Klick öffnet Detailseite) */}
      <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500" data-testid="employee-list-empty">
            <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm">
              {searchTerm.trim()
                ? `Keine Treffer für „${searchTerm.trim()}".`
                : (
                  <>
                    Keine Mitarbeiter in dieser Ansicht. Über die Schaltfläche
                    <em className="not-italic font-medium text-slate-700">
                      {" "}{"\u201E"}Mitarbeiter hinzufügen{"\u201C"}{" "}
                    </em>
                    anlegen.
                  </>
                )}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((e) => {
              const stats = assignmentsByEmp[e.id] || {};
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => navigate(`/unternehmer/mitarbeiter/${e.id}`)}
                  data-testid={`employee-row-${e.id}`}
                  className={`w-full text-left px-6 py-4 flex items-center justify-between gap-4 hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50 transition-colors ${e.archived ? "opacity-60" : ""}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    {e.photo_url ? (
                      <img
                        src={e.photo_url}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover bg-slate-100"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-sm font-medium">
                        {(e.first_name?.[0] || "") + (e.last_name?.[0] || "")}
                      </div>
                    )}
                    <div className="min-w-0 flex items-center gap-2 flex-wrap">
                      <div
                        className="font-medium text-slate-900 truncate"
                        data-testid={`employee-name-${e.id}`}
                      >
                        {e.first_name} {e.last_name}
                      </div>
                      {e.is_demo && (
                        <Badge className="bg-sky-100 hover:bg-sky-100 text-sky-700 text-[10px] uppercase tracking-wider">
                          Demo
                        </Badge>
                      )}
                      {e.archived && (
                        <Badge className="bg-slate-100 hover:bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider">
                          Archiviert
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {e.archived ? (
                      <span
                        className="w-14 h-14 inline-flex items-center justify-center text-2xl text-slate-300 select-none"
                        title="Archiviert – kein aktiver Ampelstatus"
                        aria-label="Archiviert – kein aktiver Ampelstatus"
                        data-testid={`employee-ampel-archived-${e.id}`}
                      >
                        —
                      </span>
                    ) : (
                      <AmpelStatus stats={stats} employeeId={e.id} />
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-400" aria-hidden="true" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <EmployeeForm
        key="new-employee"
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={null}
        einsatzbereiche={einsatzbereiche}
        vehicleCategories={vehicleCategories}
        onSaved={reload}
      />

      <MitarbeiterZugangPrint
        open={zugangPrintOpen}
        onOpenChange={setZugangPrintOpen}
        companyName={session?.company_name}
        companyCode={session?.company_code}
      />
    </div>
  );
}
